import React, {useState, useEffect, useRef} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ScrollView,
  Keyboard,
  DeviceEventEmitter,
  Pressable,
} from 'react-native';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, borderRadius, shadows, typography} from '~constants/design';
import {FadeIn} from '~components/MicroInteractions';
import {BigSearchIcon, LocationIcon, CalendarIcon, FunnelIcon} from '~assets/icons';
import ModernSpinner from '~components/ModernSpinner/ModernSpinner';
import Config from 'react-native-config';
import {haptics} from '~Utils/haptics';
import {BASE_API_URL, GOOGLE_API_KEY} from '@env';
import {useTranslation} from 'react-i18next';
import {useOptimizedSearch} from '~hooks/performance/useOptimizedSearch';
import {useMapsContext} from '~providers/maps/zustand';

interface ModernEventSearchProps {
  globalInputValue: string;
  setGlobalInputValue: (value: string) => void;
  onFilterPress?: () => void;
  selectedTimeframe?: any;
  selectedEventType?: any;
  isForKids?: boolean;
  radius?: number;
  timeframes?: any[];
  eventTypes?: any[];
}

const ModernEventSearch: React.FC<ModernEventSearchProps> = ({
  globalInputValue,
  setGlobalInputValue,
  onFilterPress,
  selectedTimeframe,
  selectedEventType,
  isForKids,
  radius,
  timeframes,
  eventTypes,
}) => {
  const {colors} = useTheme();
  const {t} = useTranslation();
  const {currentPositionState, setCurrentPositionState} = useMapsContext();

  const [isFocused, setIsFocused] = useState(false);
  const [locationPlaceholder, setLocationPlaceholder] = useState<string | null>(null);
  const latestCountry = useRef<string | undefined>(currentPositionState?.country);

  // Derive a readable location placeholder (short address or country)
  useEffect(() => {
    if (currentPositionState?.address) {
      const trimmed =
        currentPositionState.address.length > 30
          ? currentPositionState.address.slice(0, 27) + '...'
          : currentPositionState.address;
      setLocationPlaceholder(trimmed);
      latestCountry.current = currentPositionState.country;
    } else if (currentPositionState?.country) {
      setLocationPlaceholder(currentPositionState.country);
      latestCountry.current = currentPositionState.country;
    }
  }, [currentPositionState?.address, currentPositionState?.country]);

  // Listen for homeLocationChange events to update placeholder when user selects via search
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('homeLocationChange', (data: any) => {
      // Prefer existing currentPositionState auto update; fallback to country in event
      if (data?.country && data.country !== latestCountry.current) {
        setLocationPlaceholder(data.country);
        latestCountry.current = data.country;
      }
    });
    return () => {
      sub.remove();
    };
  }, []);

  // Use optimized search hook
  const {
    inputValue,
    setInputValue,
    showSuggestions,
    setShowSuggestions,
    suggestions,
    isLoading,
    handleLocationSelect: optimizedLocationSelect,
    handleEventSelect: optimizedEventSelect,
  } = useOptimizedSearch({
    onLocationSelect: async (placeId: string) => {
      haptics.light();
      setShowSuggestions(false);
      Keyboard.dismiss();

      try {
        // Emit loading start event
        DeviceEventEmitter.emit('mapLoadingStart');

        const apiKey = GOOGLE_API_KEY;
        const response = await fetch(
          `${BASE_API_URL}/google-places/details?place_id=${placeId}&key=${apiKey}`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'OK') {
          const location = data.result;
          setInputValue('');

          // Extract country from address_components
          let country = null;
          if (location.address_components && location.address_components.length > 0) {
            const countryComponent = location.address_components.find((component: any) =>
              component.types.includes('country'),
            );
            country = countryComponent ? countryComponent.long_name : null;
          }
          setCurrentPositionState({
            latitude: location.geometry.location.lat,
            longitude: location.geometry.location.lng,
            address: '',
            country: country,
          });

          DeviceEventEmitter.emit('homeLocationChange', {
            lat: location.geometry.location.lat,
            long: location.geometry.location.lng,
            country: country,
          });
        }
      } catch (error) {
        console.error('Error fetching location details:', error);
        // Emit loading end event on error
        DeviceEventEmitter.emit('mapLoadingEnd');
      }
    },
    onEventSelect: (event: any) => {
      haptics.light();
      setShowSuggestions(false);
      Keyboard.dismiss();
      setInputValue('');
      DeviceEventEmitter.emit('eventSelection', {event});
    },
    debounceMs: 300,
    enablePrefetch: true,
    enableOptimistic: true,
  });

  // Calculate number of active filters
  const getActiveFilterCount = () => {
    let count = 0;
    // Check if selectedTimeframe is not the first item (index 0) in timeframes array
    if (selectedTimeframe && timeframes && timeframes.findIndex(tf => tf.id === selectedTimeframe.id) !== 0) {
      count++;
    }
    // Check if selectedEventType is not the first item (index 0) in eventTypes array
    if (selectedEventType && eventTypes && eventTypes.findIndex(et => et.id === selectedEventType.id) !== 0) {
      count++;
    }
    if (isForKids) {
      count++;
    }
    return count;
  };

  const activeFilterCount = getActiveFilterCount();
  const hasActiveFilters = activeFilterCount > 0;

  // Sync with global input value
  useEffect(() => {
    setGlobalInputValue(inputValue);
  }, [inputValue, setGlobalInputValue]);

  const handleFilterPress = () => {
    haptics.light();
    onFilterPress?.();
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleDismiss = () => {
    setShowSuggestions(false);
    if (Keyboard.isVisible()) {
    Keyboard.dismiss();
    }
  };

  // Separate location and event suggestions
  const locationSuggestions = suggestions.filter((item: any) => item.type === 'location');
  const eventSuggestions = suggestions.filter((item: any) => item.type === 'event');

  return (
    <TouchableWithoutFeedback onPress={handleDismiss}>
      <View style={{paddingHorizontal: spacing.md, zIndex: 1000}}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing.sm,
          }}>
          {/* Search Input Container */}
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: colors.surface,
              borderRadius: borderRadius.lg,
              borderWidth: 1,
              borderColor: isFocused ? colors.primary : colors.border,
              paddingHorizontal: spacing.sm,
              paddingVertical: spacing.xs,
              ...shadows.sm,
            }}>
            {isLoading ? <ModernSpinner size={28} variant="circular" /> : <BigSearchIcon />}

            <TextInput
              style={{
                flex: 1,
                fontSize: typography.fontSize.sm,
                color: colors.textPrimary,
                marginLeft: spacing.xs,
                fontWeight: typography.fontWeight.medium,
                height: 48,
              }}
              // Dynamic placeholder shows current location context
              placeholder={locationPlaceholder ? `${locationPlaceholder}` : 'Search events or locations...'}
              placeholderTextColor={colors.textSecondary}
              value={inputValue}
              onChangeText={setInputValue}
              onFocus={handleFocus}
              onBlur={handleBlur}
              returnKeyType="search"
              onSubmitEditing={handleDismiss}
            />
          </View>

          {/* Filter Button */}
          <Pressable
            onPress={handleFilterPress}
            style={({pressed}) => ({
              width: 48,
              height: 48,
              borderRadius: borderRadius.lg,
              backgroundColor: hasActiveFilters ? colors.statusOrange : pressed ? colors.border : colors.surface,
              borderWidth: 1,
              borderColor: hasActiveFilters ? colors.statusOrange : colors.border,
              alignItems: 'center',
              justifyContent: 'center',
              ...shadows.sm,
              transform: [{scale: pressed ? 0.95 : 1}],
            })}>
            <View style={{position: 'relative'}}>
              {/* Filter Icon - funnel icon */}
              <FunnelIcon color={hasActiveFilters ? colors.white : colors.textPrimary} size={20} />

              {/* Filter Count Badge */}
              {activeFilterCount > 0 && (
                <View
                  style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: colors.error,
                    borderRadius: 10,
                    minWidth: 20,
                    height: 20,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 2,
                    borderColor: colors.background,
                  }}>
                  <Text
                    style={{
                      color: colors.white,
                      fontSize: 12,
                      fontWeight: 'bold',
                      textAlign: 'center',
                    }}>
                    {activeFilterCount}
                  </Text>
                </View>
              )}
            </View>
          </Pressable>
        </View>

        {/* Suggestions Dropdown - Positioned to cover filter buttons */}
        {showSuggestions && (locationSuggestions.length > 0 || eventSuggestions.length > 0) && (
          <FadeIn duration={200}>
            <View
              style={{
                position: 'absolute',
                top: 5, // Position right below the search bar
                left: 0,
                right: 0,
                backgroundColor: colors.surface,
                borderRadius: borderRadius.lg,
                borderWidth: 1,
                borderColor: colors.border,
                maxHeight: 300,
                ...shadows.lg,
                zIndex: 3000, // Higher zIndex to cover filter buttons
                marginHorizontal: spacing.md,
              }}>
              <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                {/* Location Results */}
                {locationSuggestions.map((item: any, index: number) => (
                  <TouchableOpacity
                    key={`location-${index}`}
                    onPress={() => optimizedLocationSelect(item.place_id)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: spacing.md,
                      borderBottomWidth: index < locationSuggestions.length - 1 || eventSuggestions.length > 0 ? 1 : 0,
                      borderBottomColor: colors.border,
                    }}>
                    <LocationIcon />
                    <Text
                      style={{
                        flex: 1,
                        fontSize: typography.fontSize.sm,
                        color: colors.textPrimary,
                        fontWeight: typography.fontWeight.medium,
                        marginLeft: spacing.sm,
                      }}
                      numberOfLines={2}>
                      {item.description}
                    </Text>
                  </TouchableOpacity>
                ))}

                {/* Event Results */}
                {eventSuggestions.map((item: any, index: number) => (
                  <TouchableOpacity
                    key={`event-${index}`}
                    onPress={() => optimizedEventSelect(item)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: spacing.md,
                      borderBottomWidth: index < eventSuggestions.length - 1 ? 1 : 0,
                      borderBottomColor: colors.border,
                    }}>
                    <CalendarIcon color={colors.primary} />
                    <Text
                      style={{
                        flex: 1,
                        fontSize: typography.fontSize.sm,
                        color: colors.textPrimary,
                        fontWeight: typography.fontWeight.medium,
                        marginLeft: spacing.sm,
                      }}
                      numberOfLines={2}>
                      {item.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </FadeIn>
        )}
      </View>
    </TouchableWithoutFeedback>
  );
};

export default ModernEventSearch;
